import styles from './EventTableFilters.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { Button } from '~/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';

interface IFormik {
  date: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  };
  events: { id: number; name: string; display_name: string; event: string }[];
  types: { id: number; name: string; display_name: string }[];
}

interface IFilterProps {
  setFilters: ({
    dates,
    type_ids,
    events,
  }: {
    dates: [Dayjs | null, Dayjs | null];
    type_ids: number[];
    events: string[];
  }) => void;
}

const EventTableFilters = ({ setFilters }: IFilterProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const [formattedEvents, setFormattedEvents] = useState([]);
  const [searchParams] = useSearchParams();
  const dateParams = searchParams.get('date');
  const navigate = useNavigate();

  const { data: eventsData, isLoading: isEventDataLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/event-logs-events');
      return data;
    },
    queryKey: ['filter-events'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: eventsTypes, isLoading: isEventTypesLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/event-logs-types');
      return data;
    },
    queryKey: ['filter-types'],
    onError: error => {
      toastError(error);
    },
  });

  const {
    values,
    errors,
    touched,
    submitForm,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = useFormik<IFormik>({
    initialValues: {
      date: {
        startDate: null,
        endDate: null,
      },
      events: [],
      types: [],
    },
    onSubmit: () => {
      const startDate = values.date.startDate
        ? dayjs(values?.date?.startDate)
        : null;
      const endDate = values.date.endDate ? dayjs(values?.date?.endDate) : null;
      const type_ids = values.types.map(item => item.id);
      const events = values.events.map(item => item.event);
      searchParams.delete('date');
      navigate({ search: searchParams.toString() });
      setFilters({
        dates: [startDate, endDate],
        type_ids,
        events,
      });
    },
  });

  useEffect(() => {
    if (!eventsData || eventsData.length === 0) return;
    const newData = eventsData.map((item: { display_name: string }) => {
      return {
        name: item.display_name,
        ...item,
      };
    });

    setFormattedEvents(newData);
  }, [eventsData]);

  useEffect(() => {
    if (!dateParams) return;

    setFieldValue(
      'date.startDate',
      dayjs(dateParams, 'DD.MM.YYYY').startOf('day')
    );
    setFieldValue('date.endDate', dayjs(dateParams, 'DD.MM.YYYY').endOf('day'));
  }, [dateParams]);

  return (
    <div className={styles.filterWrapper}>
      <div className={styles.selectsWrapper}>
        <div className={styles.dateWrapper}>
          <CustomDatePicker
            title={t('event_date')}
            value={[values.date.startDate, values.date.endDate]}
            placeholder={[t('date'), t('date')]}
            showPresets={true}
            disabledDate={current => current && current.isAfter(dayjs(), 'day')}
            onBlur={() => setFieldTouched('date.startDate', true)}
            onChange={(name, value) => {
              if (name === 'start_date') {
                setFieldValue('date.startDate', value);
              } else if (name === 'end_date') {
                setFieldValue('date.endDate', value);
              }
            }}
          />
        </div>
        <CustomSelect
          placeholder={t('to_choose')}
          onChange={value => {
            setFieldValue('events', value);
          }}
          label={t('event')}
          value={values.events}
          disabled={isEventDataLoading}
          options={formattedEvents || []}
          multiselect
          error={touched.events ? (errors.events as string) : undefined}
          onTouch={() => setFieldTouched('events', true)}
          className={styles.select}
          required
        />
      </div>
      <div className={styles.selectsWrapper}>
        <CustomSelect
          label={t('event_type')}
          placeholder={t('event_type_placeholder')}
          value={values.types}
          options={eventsTypes || []}
          onChange={value => setFieldValue('types', value)}
          disabled={isEventTypesLoading}
          multiselect
          className={styles.select}
        />
      </div>
      <div className={styles.btnWrapper}>
        <Button
          text={t('apply_filter')}
          type="button"
          onClick={submitForm}
          className={styles.btnSubmit}
          disabled={
            !values.date.startDate &&
            !values.date.endDate &&
            !values.events.length &&
            !values.types.length
          }
        />
        <Button
          text={t('reset_filters')}
          type="button"
          onClick={() => {
            resetForm();
            setFilters({ dates: [null, null], type_ids: [], events: [] });
          }}
          className={styles.btnReset}
        />
      </div>
    </div>
  );
};

export default EventTableFilters;
